import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const PLANS = [
  {
    id: "single",
    name: "Single Track",
    price: "€3",
    period: "one-time",
    features: ["1 downloadable track", "High-quality MP3", "No subscription", "Great for a taste"],
    featured: false,
  },
  {
    id: "monthly",
    name: "Monthly Subscription",
    price: "€9",
    period: "/month",
    features: ["4 tracks per month", "Community chat access", "Unlimited playlists", "Auto-renewal (test)"],
    featured: true,
    badge: "Most Popular",
  },
  {
    id: "annual",
    name: "Annual Subscription",
    price: "€99",
    period: "/year",
    features: ["50 tracks per year", "Community + events", "Save 2 months", "Early access to new releases"],
    featured: false,
    badge: "Best Value",
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(null);

  const handleBuy = async (planId, method = "stripe") => {
    if (!user) { toast.error("Sign in to purchase"); nav("/login?next=/pricing"); return; }
    setLoading(`${planId}-${method}`);
    try {
      if (method === "paypal") {
        const { data } = await api.post("/payments/paypal/create", {
          package_id: planId,
          origin_url: window.location.origin,
        });
        window.location.href = data.approval_url;
        return;
      }
      const { data } = await api.post("/payments/checkout", {
        package_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(null);
    }
  };

  return (
    <div className="container-x py-16">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <div className="eyebrow mb-4">// Choose your groove</div>
        <h1 className="section-heading mb-4">Plans & Subscriptions</h1>
        <p className="text-white/60 text-lg">Unlock the 2B FUNK sound. Transparent pricing, cancel anytime.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {PLANS.map((p) => (
          <div key={p.id} className={p.featured ? "beam-border" : ""}>
            <div
              className={`h-full p-8 flex flex-col border ${p.featured ? "bg-[#0d0d12] border-transparent" : "bg-[#121217] border-white/5"}`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.badge && (
                <div className="mb-4 inline-block self-start text-[10px] font-mono-plex uppercase tracking-[0.2em] text-[#B3FF00] border border-[#B3FF00]/40 px-2 py-1">
                  {p.badge}
                </div>
              )}
              <h3 className="font-display font-bold text-2xl uppercase">{p.name}</h3>
              <div className="mt-4 mb-6 flex items-baseline gap-2">
                <span className="font-display font-black text-5xl text-[#FF5E00]">{p.price}</span>
                <span className="text-sm font-mono-plex uppercase tracking-widest text-white/50">{p.period}</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-white/80">
                    <Check className="w-4 h-4 text-[#B3FF00] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleBuy(p.id, "stripe")}
                disabled={loading?.startsWith(p.id)}
                className={p.featured ? "btn-primary w-full" : "btn-outline w-full"}
                data-testid={`buy-plan-${p.id}-stripe`}
              >
                {loading === `${p.id}-stripe` ? "Redirecting..." : "Pay with Stripe"}
              </button>
              <button
                onClick={() => handleBuy(p.id, "paypal")}
                disabled={loading?.startsWith(p.id)}
                className="mt-2 w-full py-3 border border-white/10 text-white/70 font-display uppercase tracking-widest text-xs hover:border-[#B3FF00]/50 hover:text-[#B3FF00] disabled:opacity-50"
                data-testid={`buy-plan-${p.id}-paypal`}
              >
                {loading === `${p.id}-paypal` ? "Redirecting..." : "Pay with PayPal"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-white/40 font-mono-plex uppercase tracking-widest mt-12">
        Secure payments via Stripe · Test card: 4242 4242 4242 4242
      </p>
      <div className="h-32" />
    </div>
  );
}
